import { useEffect, useState } from "react";
import { api, type Note, type NoteKind } from "@/lib/api";

interface UseNotesResult {
  notes: Note[];
  loading: boolean;
  error: string | null;
}

interface UseCreateNoteOptions {
  kind: NoteKind;
  body: string;
  fileId?: string | null;
  feedToAi?: boolean;
}

interface UseUpdateNoteOptions {
  kind?: NoteKind;
  body?: string;
  feedToAi?: boolean;
}

export function useNotes(projectId: string): UseNotesResult {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedNotes = await api.listNotes(projectId);
        setNotes(fetchedNotes);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load notes";
        setError(message);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [projectId]);

  return { notes, loading, error };
}

export function useCreateNote(projectId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createNote = async (options: UseCreateNoteOptions): Promise<Note | null> => {
    try {
      setLoading(true);
      setError(null);
      const newNote = await api.createNote(projectId, options);
      return newNote;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create note";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createNote, loading, error };
}

export function useUpdateNote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateNote = async (
    noteId: string,
    options: UseUpdateNoteOptions,
  ): Promise<Note | null> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await api.updateNote(noteId, options);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update note";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { updateNote, loading, error };
}

export function useDeleteNote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteNote = async (noteId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteNote(noteId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete note";
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { deleteNote, loading, error };
}
